import { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { getAdminUsers, updateUserRole, formatApiError } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Users, Search, Shield, GraduationCap, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchUsers = async () => {
    try {
      const data = await getAdminUsers();
      setUsers(data);
      setFilteredUsers(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    let result = users;
    if (search) {
      result = result.filter(u => 
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (roleFilter !== 'all') {
      result = result.filter(u => u.role === roleFilter);
    }
    setFilteredUsers(result);
  }, [search, roleFilter, users]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      toast.success('Rol actualizado');
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield className="h-4 w-4 text-[#8B1538]" />;
      case 'teacher': return <BookOpen className="h-4 w-4 text-[#B8860B]" />;
      default: return <GraduationCap className="h-4 w-4 text-[#2D6A4F]" />;
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-[#8B1538]/10 text-[#8B1538]',
      teacher: 'bg-[#B8860B]/10 text-[#B8860B]',
      student: 'bg-[#2D6A4F]/10 text-[#2D6A4F]'
    };
    const labels = { admin: 'Admin', teacher: 'Profesor', student: 'Estudiante' };
    return (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[role] || styles.student}`}>
        {labels[role] || role}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="admin-users-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1A1A]">
            Gestión de Usuarios
          </h1>
          <p className="text-[#737373] mt-2">{users.length} usuarios registrados</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#737373]" />
            <Input
              data-testid="search-users-input"
              placeholder="Buscar por nombre o email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 border-[#E5DFD3]"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger data-testid="role-filter-select" className="w-full sm:w-48 border-[#E5DFD3]">
              <SelectValue placeholder="Filtrar por rol" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los roles</SelectItem>
              <SelectItem value="admin">Administradores</SelectItem>
              <SelectItem value="teacher">Profesores</SelectItem>
              <SelectItem value="student">Estudiantes</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Users Table */}
        <Card className="bg-white border border-[#E5DFD3] shadow-sm">
          <CardContent className="pt-6">
            {filteredUsers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E5DFD3]">
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#737373]">Usuario</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#737373]">Email</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#737373]">Rol</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#737373]">Fecha de Registro</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#737373]">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id} data-testid={`user-row-${user.id}`} className="border-b border-[#E5DFD3] table-row-hover">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full avatar-placeholder flex items-center justify-center text-white font-medium">
                              {user.name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <span className="font-medium text-[#1A1A1A]">{user.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-[#4A4A4A]">{user.email}</td>
                        <td className="py-3 px-4">{getRoleBadge(user.role)}</td>
                        <td className="py-3 px-4 text-sm text-[#737373]">
                          {new Date(user.created_at).toLocaleDateString('es-ES')}
                        </td>
                        <td className="py-3 px-4">
                          <Select 
                            value={user.role} 
                            onValueChange={(v) => handleRoleChange(user.id, v)}
                          >
                            <SelectTrigger className="w-36 h-8 text-sm border-[#E5DFD3]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="student">Estudiante</SelectItem>
                              <SelectItem value="teacher">Profesor</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="h-12 w-12 mx-auto text-[#D9B48F] mb-3" />
                <p className="text-[#737373]">No se encontraron usuarios</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
